// @ts-nocheck
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { PAYMENT_CHECKOUT_SESSION_ID } from 'dpl/constants/query_params';
import PaymentMethodOption from 'dpl/buyer_account/components/PaymentMethodOption';
import {
  buyerPaymentMethodQueryDefinition,
  chargeSavedPaymentMethodV2Mutation
} from 'dpl/buyer_account/utils/resourceDefinitions';
import { GDButtonV1 } from 'dpl/common/core/GDButton/GDButtonV1';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useToastNotifications from 'dpl/shared/hooks/useToastNotifications';
import { isNativeAppUA } from 'dpl/shared/utils';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { stripePromise } from 'dpl/util/stripePromise';
import { trackPaymentConversion } from 'dpl/breeder_dashboard/payments/utils/analytics';

import StripeChargeModalFooter from './StripeChargeModalFooter';
import { CONFIRM_PAYMENT_LABEL, PAYMENT_TIER_TYPE } from './constants';

const IS_NATIVE_APP = isNativeAppUA();

const PAYMENT_CARD_CLASSES = 'ba b--gray-300 br12 ph4 pv4';

type PaymentTierType =
  (typeof PAYMENT_TIER_TYPE)[keyof typeof PAYMENT_TIER_TYPE];

interface PrivateFormComponentProps {
  analytics?: {
    view?: string;
    viewFeature?: string;
    originView?: string;
    originViewFeature?: string;
    originViewFeatureLocation?: string;
  };
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  externalId: string;
  checkoutSessionIdRef: React.RefObject<string>;
  id: number | string;
  onChange?: ((data: any) => void) | null;
  onSubmit?: (() => void) | null;
  isFetching: boolean;
  breederProfileId?: any;
  buyerProfileId?: any;
  paymentCategory: string;
  paymentAmount?: number | string | null;
  paymentMethod?: any;
  paymentTier?: PaymentTierType;
  billingDetails?: { email?: string; name?: string };
  // what happens after paying, shown between the button and the terms
  footerLead?: React.ReactNode;
}

function PrivateFormComponent({
  analytics = {},
  children = null,
  className = '',
  disabled = false,
  externalId,
  checkoutSessionIdRef,
  id,
  onChange = null,
  onSubmit = null,
  isFetching,
  breederProfileId,
  buyerProfileId,
  paymentCategory,
  paymentAmount = null,
  paymentMethod,
  paymentTier,
  billingDetails = {},
  footerLead = null
}: PrivateFormComponentProps) {
  const elements = useElements();
  const stripe = useStripe();
  const { pushToastNotification } = useToastNotifications();

  const forceNewMethod = useSelector(
    ({ queryParams }: any) => queryParams.force_new_method === 'true'
  );

  // null until the user explicitly picks an option; before that, selection is
  // derived from the fetched default payment method. Deriving (rather than
  // syncing via an effect) keeps the PaymentElement from mounting for a single
  // render and then unmounting, which tears down Stripe's iframe
  // mid-initialization and makes Stripe.js throw "Frame not initialized".
  const [userSelectedDefault, setUserSelectedDefault] = useState<
    boolean | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submissionPageUrl = new URL(
    `/pay/${externalId}/submission?${PAYMENT_CHECKOUT_SESSION_ID}=${checkoutSessionIdRef.current}`,
    window.location.origin
  ).href;

  const successPageUrl = new URL(
    `/pay/${externalId}/success?${PAYMENT_CHECKOUT_SESSION_ID}=${checkoutSessionIdRef.current}`,
    window.location.origin
  ).href;

  const failurePageUrl = new URL(
    `/pay/${externalId}/failure?${PAYMENT_CHECKOUT_SESSION_ID}=${checkoutSessionIdRef.current}`,
    window.location.origin
  ).href;

  const { data, isFetching: isDefaultPaymentMethodFetching } = useResourceQuery(
    buyerPaymentMethodQueryDefinition()
  );
  const defaultPaymentMethod = data?.data?.stripe_payment_method;

  const isDefaultPaymentSelected =
    userSelectedDefault ?? (!forceNewMethod && Boolean(defaultPaymentMethod));

  // * When the form is submitting, we could not find a way to make the form elements not clickable. // * So to prevent the customer from changing stuff part way through submitting, we just don't send // * `onChange` events. On submit fail, this can cause a de-sync between the UI and the actual payment // * request record.
  function handleChange(stripeData: any) {
    if (!isSubmitting) {
      onChange?.(stripeData);
    }
  }

  useEffect(() => {
    if (isDefaultPaymentSelected) {
      handleChange({ value: { type: defaultPaymentMethod.type } });
    }
  }, [isDefaultPaymentSelected]);

  const { mutateAsync: chargeSavedPaymentMethodAsync } = useResourceMutation(
    chargeSavedPaymentMethodV2Mutation(id)
  );

  async function chargeSavedPaymentMethod() {
    try {
      const {
        data: {
          status,
          payment_intent: {
            status: paymentIntentStatus,
            client_secret: clientSecret
          }
        }
      } = await chargeSavedPaymentMethodAsync();

      if (status === 'ok') {
        if (paymentIntentStatus === 'requires_action') {
          const { error } = await stripe!.confirmCardPayment(clientSecret, {
            return_url: successPageUrl
          });

          if (error) {
            pushToastNotification({ message: error.message, type: 'error' });
            sendRudderstackEvent(
              ANALYTICS_EVENTS.PAYMENT_REQUEST_FAILED_BY_BUYER,
              {
                view:
                  (analytics as any).view || ANALYTICS_VIEWS.PAYMENT_DETAILS,
                view_feature:
                  (analytics as any).viewFeature ||
                  ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
                buyer_profile_id: buyerProfileId,
                breeder_profile_id: breederProfileId,
                payment_request_id: id,
                payment_type: paymentCategory,
                checkout_session_id: checkoutSessionIdRef.current,
                failure_reason: error.message
              }
            );

            setIsSubmitting(false);

            return;
          }
        }

        await trackPaymentConversion(paymentCategory, paymentAmount);
        window.location.assign(successPageUrl);
      } else if (status === 'balance_check_pending') {
        // try again after giving the balance refresh time to complete
        setTimeout(chargeSavedPaymentMethod, 2000);
      } else if (status === 'balance_check_failed') {
        window.location.assign(failurePageUrl);
      } else {
        // TODO: notify bugsnag here about unhandled status
        pushToastNotification({
          type: 'error',
          message: 'There was a problem charging your saved payment method.'
        });
        setIsSubmitting(false);
      }
    } catch {
      pushToastNotification({
        type: 'error',
        message: 'There was a problem charging your saved payment method.'
      });
      setIsSubmitting(false);
    }
  }

  async function handleSubmit(e: React.MouseEvent) {
    try {
      e.preventDefault();

      setIsSubmitting(true);

      sendRudderstackEvent(
        ANALYTICS_EVENTS.PAYMENT_CHECKOUT_SUBMITTED_BY_BUYER,
        {
          view: (analytics as any).view || ANALYTICS_VIEWS.PAYMENT_DETAILS,
          view_feature:
            (analytics as any).viewFeature ||
            ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
          origin_view: (analytics as any).originView,
          origin_view_feature: (analytics as any).originViewFeature,
          origin_view_feature_location: (analytics as any)
            .originViewFeatureLocation,
          payment_request_id: id,
          breeder_profile_id: breederProfileId,
          buyer_profile_id: buyerProfileId,
          payment_type: paymentCategory,
          checkout_session_id: checkoutSessionIdRef.current
        }
      );

      onSubmit?.();

      if (isDefaultPaymentSelected) {
        chargeSavedPaymentMethod();
      } else {
        const { error } = await stripe!.confirmSetup({
          confirmParams: { return_url: submissionPageUrl },
          elements: elements!
        });

        if (error && error.type !== 'validation_error') {
          pushToastNotification({
            type: 'error',
            message: error.message
          });
          setIsSubmitting(false);
        }
      }
    } catch {
      pushToastNotification({
        type: 'error',
        message: 'There was a problem charging your saved payment method.'
      });
    }
  }

  function handleOpenInBrowser() {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'OpenInBrowser',
        value: window.location.href
      })
    );
  }

  const shouldDisplayPaymentMethodOptions =
    !forceNewMethod && Boolean(defaultPaymentMethod);

  return (
    <form className={className}>
      <LoadingWrapper isLoading={isDefaultPaymentMethodFetching}>
        {shouldDisplayPaymentMethodOptions && (
          <section className={PAYMENT_CARD_CLASSES}>
            <h2 className="font-serif font-18 lh-title mb4">
              Select payment method
            </h2>
            <PaymentMethodOption
              active={isDefaultPaymentSelected}
              paymentMethod={defaultPaymentMethod}
              onClick={() => {
                setUserSelectedDefault(true);
              }}
            />
            <PaymentMethodOption
              className="mt2"
              active={!isDefaultPaymentSelected}
              onClick={() => {
                setUserSelectedDefault(false);
              }}
            />
          </section>
        )}
        {!isDefaultPaymentSelected && (
          <section
            className={classnames(PAYMENT_CARD_CLASSES, {
              mt6: shouldDisplayPaymentMethodOptions
            })}
          >
            <h2 className="font-serif font-18 lh-title mb4">
              {IS_NATIVE_APP && 'Payment method'}
              {!IS_NATIVE_APP &&
                (shouldDisplayPaymentMethodOptions
                  ? 'Add new payment method'
                  : 'Select payment method')}
            </h2>
            <PaymentElement
              options={{
                readOnly: isFetching || isSubmitting,
                defaultValues: {
                  billingDetails
                },
                // Link is a card-tab wallet, not one of our
                // payment_method_types, so this option is the sole
                // enforcement point keeping Link (and its enrollment
                // prompt) out of checkout (CORE-5883)
                wallets: { link: 'never' },
                terms: {
                  applePay: 'never',
                  auBecsDebit: 'never',
                  bancontact: 'never',
                  card: 'never',
                  cashapp: 'never',
                  googlePay: 'never',
                  ideal: 'never',
                  paypal: 'never',
                  sepaDebit: 'never',
                  sofort: 'never',
                  usBankAccount: 'never'
                }
              }}
              onChange={handleChange}
            />
            {IS_NATIVE_APP && (
              <p className="f1 mt4">
                To pay by bank transfer (ACH) please{' '}
                <button
                  type="button"
                  className="link underline primary"
                  onClick={handleOpenInBrowser}
                >
                  visit our website
                </button>
              </p>
            )}
          </section>
        )}
        {children}
        <div className="pt2">
          <GDButtonV1
            disabled={disabled || isSubmitting || isFetching}
            type="submit"
            onClick={handleSubmit}
            className="PayNowButton w-100"
          >
            {CONFIRM_PAYMENT_LABEL}
          </GDButtonV1>
          {footerLead}
          <StripeChargeModalFooter
            paymentMethod={paymentMethod}
            paymentTier={paymentTier}
            hideProtectedLine
          />
        </div>
      </LoadingWrapper>
    </form>
  );
}

interface CompletePaymentRequestFormV4Props
  extends Omit<PrivateFormComponentProps, 'analytics'> {
  clientSecret: string;
  analytics?: any;
  [key: string]: any;
}

export function CompletePaymentRequestFormV4({
  clientSecret,
  ...args
}: CompletePaymentRequestFormV4Props) {
  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        spacingUnit: '6px',
        borderRadius: '8px',
        fontFamily:
          'GT Walsheim Pro, avenir next, avenir, helvetica neue, helvetica, arial, sans-serif',
        colorPrimary: '#0057de',
        colorText: '#283e52'
      },
      rules: {
        '.Tab': {
          fontWeight: '500',
          boxShadow: 'none',
          borderColor: '#e9e9ed',
          padding: '16px'
        },
        '.Tab--selected': {
          fontWeight: '500',
          boxShadow: 'none',
          borderColor: '#283e52'
        },
        '.Input': {
          boxShadow: 'none',
          borderColor: '#e9e9ed',
          padding: '14px 16px'
        },
        '.Input:focus': {
          borderColor: '#283e52',
          boxShadow: 'none'
        }
      }
    }
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <PrivateFormComponent {...args} />
    </Elements>
  );
}
